import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
    useExamPerformFailuresCount,
    useExamPerformFailuresData,
    useDismissExamPerformFailure,
    invalidateAllWidgetQueries
} from '../../../hooks/useWidgetData';
import { useSelectedDashboardFacility } from '../../../hooks/useSelectedDashboardFacility';
import { toast } from '@rickylandino/react-messages';
import { CheckCircle } from 'lucide-react';
import { Table, Modal } from 'antd';
import Globals from '../../../config/globals';
import ReconcilePerformFailureModal from './ExamPerformFailures/ReconcilePerformFailureModal';

/**
 * ExamPerformFailuresWidget component
 * System-wide view of all exam perform failures with Reconcile and Dismiss actions.
 * @param {Object} props
 * @param {boolean} [props.isCollapsed=false]
 * @param {boolean} [props.isExpanded=false]
 * @param {Function} [props.setIsLoading]
 * @param {Function} [props.setSharedState]
 * @param {boolean} [props.queryEnabled=true]
 */
export default function ExamPerformFailuresWidget({
    isCollapsed = false,
    isExpanded = false,
    setIsLoading,
    setSharedState,
    queryEnabled = true
}) {
    const queryClient = useQueryClient();
    const dismissMutation = useDismissExamPerformFailure();
    const { selectedFacilities } = useSelectedDashboardFacility();

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);

    const [showReconcileModal, setShowReconcileModal] = useState(false);
    const [failureToReconcile, setFailureToReconcile] = useState(null);

    const [showDismissModal, setShowDismissModal] = useState(false);
    const [dismissTarget, setDismissTarget] = useState(null);

    const shouldLoadFullData = !isCollapsed || isExpanded;

    const { data: countData, isLoading: isLoadingCount } = useExamPerformFailuresCount(
        (isCollapsed && !isExpanded) && queryEnabled,
        selectedFacilities
    );

    const {
        data,
        isLoading,
        isFetching,
        isError,
        error,
        refetch
    } = useExamPerformFailuresData(currentPage, pageSize, shouldLoadFullData && queryEnabled, selectedFacilities);

    const results = data?.data || [];
    const totalCount = data?.totalCount || 0;
    const count = shouldLoadFullData ? totalCount : (countData?.count || 0);

    useEffect(() => {
        if (setIsLoading && isExpanded) setIsLoading(isLoading || isFetching);
    }, [isLoading, isFetching, isExpanded, setIsLoading]);

    useEffect(() => {
        if (shouldLoadFullData && isError) {
            toast.error('Failed to fetch exam perform failures. Please try again.');
        }
    }, [shouldLoadFullData, isError, error]);

    useEffect(() => {
        if (setSharedState && refetch) setSharedState(prev => ({ ...prev, refetch }));
    }, [setSharedState, refetch]);

    const formatDate = useCallback((dateString) => {
        if (!dateString) return '�';
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'numeric', day: 'numeric', year: 'numeric'
        });
    }, []);

    const handleReconcile = useCallback((row) => {
        setFailureToReconcile(row);
        setShowReconcileModal(true);
    }, []);

    const handleDismiss = useCallback((row) => {
        setDismissTarget(row);
        setShowDismissModal(true);
    }, []);

    const handleConfirmDismiss = useCallback(async () => {
        if (!dismissTarget?.examPerformFailureId) return;
        try {
            await dismissMutation.mutateAsync(dismissTarget.examPerformFailureId);
            setShowDismissModal(false);
            setDismissTarget(null);
            toast.success('Failure dismissed');
        } catch (e) {
            console.error('Dismiss error:', e);
            toast.error('Failed to dismiss. Please try again.');
        }
    }, [dismissTarget, dismissMutation]);

    const pink = Globals.theme === 'dark' ? '#ec4899' : '#db2777';
    const pinkHover = Globals.theme === 'dark' ? '#db2777' : '#be185d';

    const columns = useMemo(() => [
        {
            title: 'Accession #',
            dataIndex: 'accessionNumber',
            key: 'accessionNumber',
            width: 160,
            render: (val) => val || '-'
        },
        {
            title: 'Patient',
            dataIndex: 'patientName',
            key: 'patientName',
            width: 160,
            render: (name, row) => name || row.patientMrn || '-'
        },
        {
            title: 'Facility',
            dataIndex: 'facilityName',
            key: 'facilityName',
            width: 160,
        },
        {
            title: 'Exam Type',
            dataIndex: 'examTypeName',
            key: 'examTypeName',
            width: 180,
            render: (val) => val || '-'
        },
        {
            title: 'Failure Reason',
            dataIndex: 'failureReason',
            key: 'failureReason',
            width: 220,
        },
        {
            title: 'Status Code',
            dataIndex: 'statusCode',
            key: 'statusCode',
            width: 100,
            render: (val) => val ?? '-'
        },
        {
            title: 'Date',
            dataIndex: 'createdDate',
            key: 'createdDate',
            width: 120,
            render: (d) => formatDate(d)
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 180,
            fixed: 'right',
            render: (_, row) => (
                <div style={{ display: 'flex', gap: 8 }}>
                    <button
                        onClick={(e) => { e.stopPropagation(); handleReconcile(row); }}
                        className="px-3 py-1.5 rounded-lg font-medium text-sm"
                        style={{ backgroundColor: pink, color: 'white', border: 'none', cursor: 'pointer' }}
                        onMouseEnter={e => e.currentTarget.style.backgroundColor = pinkHover}
                        onMouseLeave={e => e.currentTarget.style.backgroundColor = pink}
                    >
                        Reconcile
                    </button>
                    <button
                        onClick={(e) => { e.stopPropagation(); handleDismiss(row); }}
                        className="px-3 py-1.5 rounded-lg font-medium text-sm"
                        style={{
                            backgroundColor: 'transparent',
                            color: Globals.theme === 'dark' ? '#a1a1aa' : '#71717a',
                            border: `1px solid ${Globals.theme === 'dark' ? '#52525b' : '#d1d5db'}`,
                            cursor: 'pointer'
                        }}
                    >
                        Dismiss
                    </button>
                </div>
            )
        }
    ], [formatDate, handleReconcile, handleDismiss, pink, pinkHover]);

    const getCountColor = () => pink;

    // COLLAPSED STATE
    if (isCollapsed && !isExpanded) {
        if (isLoadingCount) {
            return (
                <div className="flex items-center justify-center py-4">
                    <div
                        className="animate-spin rounded-full h-8 w-8 border-b-2"
                        style={{ borderColor: Globals.theme === 'dark' ? '#a1a1aa' : '#71717a' }}
                    />
                </div>
            );
        }
        return (
            <div className="flex flex-col items-center justify-center py-4">
                <div className="text-5xl font-light mb-2" style={{ color: getCountColor() }}>
                    {count.toLocaleString()}
                </div>
                <div className="text-sm" style={{ color: Globals.theme === 'dark' ? '#a1a1aa' : '#71717a' }}>
                    {count === 1 ? 'perform failure' : 'perform failures'}
                </div>
            </div>
        );
    }

    // EMPTY STATE
    if (!isLoading && count === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-4">
                <CheckCircle className="h-16 w-16 mx-auto mb-4" style={{ color: getCountColor() }} />
                <p style={{ color: getCountColor() }}>No Exam Perform Failures Found</p>
            </div>
        );
    }

    // EXPANDED STATE
    return (
        <>
            <Table
                columns={columns}
                dataSource={results}
                rowKey="examPerformFailureId"
                pagination={{
                    current: currentPage,
                    pageSize,
                    total: totalCount,
                    onChange: (page, newPageSize) => {
                        setCurrentPage(page);
                        if (newPageSize !== pageSize) {
                            setPageSize(newPageSize);
                            setCurrentPage(1);
                        }
                    },
                    pageSizeOptions: ['25', '50', '100', '200'],
                    showSizeChanger: true,
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} failures`
                }}
                size="small"
                scroll={{ y: 500 }}
                virtual
                loading={{ spinning: (isLoading && !isExpanded) || isFetching }}
            />

            <ReconcilePerformFailureModal
                visible={showReconcileModal}
                failure={failureToReconcile}
                onSuccess={() => {
                    setShowReconcileModal(false);
                    setFailureToReconcile(null);
                }}
                onCancel={() => {
                    setShowReconcileModal(false);
                    setFailureToReconcile(null);
                }}
            />

            <Modal
                visible={showDismissModal}
                title="Dismiss Perform Failure"
                onCancel={() => { setShowDismissModal(false); setDismissTarget(null); }}
                footer={[
                    <button
                        key="cancel"
                        onClick={() => { setShowDismissModal(false); setDismissTarget(null); }}
                        className="px-4 py-1.5 rounded-lg font-medium text-sm"
                        style={{
                            backgroundColor: 'transparent',
                            border: `1px solid ${Globals.theme === 'dark' ? '#52525b' : '#d1d5db'}`,
                            cursor: 'pointer',
                            marginRight: 8
                        }}
                    >
                        Cancel
                    </button>,
                    <button
                        key="confirm"
                        onClick={handleConfirmDismiss}
                        disabled={dismissMutation.isLoading}
                        className="px-4 py-1.5 rounded-lg font-medium text-sm"
                        style={{ backgroundColor: '#ef4444', color: 'white', border: 'none', cursor: 'pointer' }}
                    >
                        Dismiss
                    </button>
                ]}
            >
                <p>
                    Dismissing accession <strong>{dismissTarget?.accessionNumber || '(no accession)'}</strong> will
                    remove it from the queue without updating any exam data.
                </p>
                <p>Are you sure?</p>
            </Modal>
        </>
    );
}
